import React, {
  ReactElement, ReactNode, useCallback, useEffect, useState,
} from 'react';
import {
  Button, Col, FormGroup, Label, Row, SearchResults, Select, SingleSelect,
} from 'idsk-react-components';
import { useParams } from 'react-router';
import PreviazanyTermin from '../../../../../model/PreviazanyTermin';
import './previazanyTerminItem.scss';
import { SafeParameterSetter } from '../../../../../model/SafeParameterSetter';
import { getTerminSearchResults } from '../../../../../client/termin/terminSearchClient';
import { useClientSearchResuls } from '../../../../../context/ClientStatusContext';
import TezaurusTerminPair from '../../../../../model/TezaurusTerminPair';
import PreviazanyTerminDetail from '../../../../../model/PreviazanyTerminDetail';
import {
  MAX_MULTISELECT_RESULT,
  SHOW_ALL_MAX_RESULT,
} from '../../../../../constants/MultiSelectConstant';

interface Props {
  setItemList: (data: PreviazanyTermin[]) => void;
  itemId: number;
  children: ReactNode;
  itemList: Array<PreviazanyTermin>;
  getFieldErrorMessage: (fieldName: string) => string;
  clearFieldErrorMessage: (fieldName: string) => void;
  clearAllFieldErrorMessages: () => void;
  initialPreviazaneTerminy: Array<PreviazanyTerminDetail>;
  setInitialPreviazaneTerminy: (data: PreviazanyTerminDetail[]) => void;
}
let searchTimeout;
const PreviazanyTerminItem = ({
  setItemList,
  itemId,
  children,
  itemList = [],
  clearFieldErrorMessage,
  clearAllFieldErrorMessages,
  getFieldErrorMessage,
  initialPreviazaneTerminy,
  setInitialPreviazaneTerminy,
}: Props): ReactElement => {
  const mapVztahToOptionValue = (vztah): string => {
    switch (vztah) {
      case 'Príbuzný termín':
        return 'PRIBUZNY';
      case 'Všeobecnejší termín':
        return 'VSEOBECNEJSI';
      case 'Podrobnejší termín':
        return 'KONKRETNEJSI';
      case 'PRIBUZNY':
        return 'PRIBUZNY';
      case 'VSEOBECNEJSI':
        return 'VSEOBECNEJSI';
      case 'KONKRETNEJSI':
        return 'KONKRETNEJSI';
      default:
        return '';
    }
  };

  const [previazanyTermin, setPreviazanyTermin] = useState<PreviazanyTermin>({
    ...itemList[itemId],
    vztah: mapVztahToOptionValue(itemList[itemId].vztah),
    previazanyTerminUuid: (itemList[itemId].uuid),
  });

  const [searchText, setSearchText] = useState<string>(
    initialPreviazaneTerminy[itemId] ? initialPreviazaneTerminy[itemId].nazov : '',
  );
  const [limit, setLimit] = useState<number>(MAX_MULTISELECT_RESULT);
  const [selectedList, setSelectedList] = useState<TezaurusTerminPair>(
    initialPreviazaneTerminy[itemId]
      ? {
        uuid: initialPreviazaneTerminy[itemId].uuid,
        nazov: initialPreviazaneTerminy[itemId].tezaurus.nazov,
        termin: initialPreviazaneTerminy[itemId].nazov,
        publikovany: true,
      }
      : null,
  );

  const [dataVyhladavanie, setDataVyhladavanie] = useState<TezaurusTerminPair[]>(
    (initialPreviazaneTerminy[itemId] && searchText === initialPreviazaneTerminy[itemId].nazov) ? [{
      uuid: initialPreviazaneTerminy[itemId].uuid,
      nazov: initialPreviazaneTerminy[itemId].tezaurus.nazov,
      termin: initialPreviazaneTerminy[itemId].nazov,
      publikovany: true,
    }] : [],
  );
  const [dataVyhladavanieLoading, setDataVyhladavanieLoading] = useState(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [visibilityMinSearchLength] = useState<number>(0);

  const {
    terminUuid,
    // @ts-ignore
  } = useParams<{ terminUuid: string}>();

  const {
    wrapClient: terminSearchResultsClient,
  } = useClientSearchResuls(getTerminSearchResults);

  const onUpdateSearchField = useCallback(async () => {
    try {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(async () => {
        setDataVyhladavanieLoading(true);
        const response = await terminSearchResultsClient(searchText, terminUuid, limit);
        const newData = response.data || [];
        if (dataVyhladavanie.length === 0) {
          setDataVyhladavanie(newData);
        }
        if (searchText === '' && selectedList === null) {
          setDataVyhladavanie(newData);
        }
        if (searchText !== '') {
          setDataVyhladavanie(newData);
        }

        if (searchText !== initialPreviazaneTerminy[itemId]?.nazov && searchText !== '') {
          setShowList(true);
        }
      }, 300);
      setDataVyhladavanieLoading(false);
    } catch (error) {
      setDataVyhladavanie([]);
      setDataVyhladavanieLoading(false);
    }
  }, [
    terminSearchResultsClient,
    searchText,
    terminUuid,
    dataVyhladavanie.length,
    selectedList,
    initialPreviazaneTerminy,
    itemId,
    limit]);

  useEffect(() => {
    onUpdateSearchField();
  }, [searchText, onUpdateSearchField]);

  const updateField: SafeParameterSetter<PreviazanyTermin> = useCallback(
    (fieldName, value) => {
      setPreviazanyTermin(prevState => ({ ...prevState, [fieldName]: value }));

      const newFormValues = itemList;
      newFormValues[itemId] = { ...previazanyTermin, [fieldName]: value };
      setItemList(newFormValues);

      if (clearFieldErrorMessage) {
        clearFieldErrorMessage(fieldName + itemId);
      }
    },
    [itemList, itemId, previazanyTermin, clearFieldErrorMessage, setItemList],
  );

  useEffect(() => {
    if (selectedList) {
      updateField('previazanyTerminUuid', selectedList.uuid);
    } else {
      updateField('previazanyTerminUuid', undefined);
      onUpdateSearchField();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList]);

  const onRemove = (): void => {
    setItemList(itemList.filter((_, index) => index !== itemId));
    setInitialPreviazaneTerminy(initialPreviazaneTerminy.filter((_, index) => index !== itemId));
    if (clearAllFieldErrorMessages) {
      clearAllFieldErrorMessages();
    }
  };

  const handleSelectedItemChange = (item: TezaurusTerminPair | null): void => {
    setSelectedList(item);
    setShowList(false);

    if (item) {
      setSearchText(item.termin);
      setDataVyhladavanie([item]);
    } else if (searchText === '') {
      setDataVyhladavanie([]);
    }
  };

  return (
    <>
      <div className="previazany-termin-item">
        <FormGroup>
          <Row>
            <Col width="two-thirds">
              <Label htmlFor="previazanyTerminVztah">Typ vzťahu</Label>
            </Col>
          </Row>
          <Row>
            <Col width="two-thirds">
              <Select
                id="previazanyTerminVztah"
                width="one-half"
                name="previazanyTerminVztah"
                value={previazanyTermin.vztah}
                onChange={({ target }) => updateField('vztah', target.value)}
                error={getFieldErrorMessage(`vztah${itemId}`)}
              >
                <option value="" disabled hidden>Vyberte</option>
                <option value="KONKRETNEJSI">Podrobnejší termín</option>
                <option value="PRIBUZNY">Príbuzný termín</option>
                <option value="VSEOBECNEJSI">Všeobecnejší termín</option>
              </Select>
            </Col>
          </Row>
          <Row className="previazany-termin-item__label">
            <Col width="two-thirds">
              <div className="previazany-termin-single-select">
                <SingleSelect
                  id="previazanyTerminSingleSelect"
                  name="previazanyTerminSingleSelect"
                  label="Termín"
                  hint="Začnite písať a vyberte z ponúkaných možností."
                  searchText={searchText}
                  setSearchText={(text) => {
                    setSearchText(text);
                    setLimit(MAX_MULTISELECT_RESULT);
                  }}
                  selectedItem={selectedList || null}
                  setSelectedItem={handleSelectedItemChange}
                  selectedItemValue={selectedList?.termin}
                  visibilityMinSearchLength={visibilityMinSearchLength}
                  showList={showList}
                  setShowList={setShowList}
                  error={getFieldErrorMessage(`previazanyTerminUuid${itemId}`)}
                />
                <SearchResults
                  loadingShowAll={dataVyhladavanieLoading}
                  className="single-select-fazet"
                  handleShowAll={() => {
                    setLimit(SHOW_ALL_MAX_RESULT);
                  }}
                  withShowAllButton={(limit === MAX_MULTISELECT_RESULT
                      && dataVyhladavanie.length === MAX_MULTISELECT_RESULT)
                    || (limit === SHOW_ALL_MAX_RESULT && dataVyhladavanie.length <= MAX_MULTISELECT_RESULT)}
                  setSearchText={(text) => {
                    setSearchText(text);
                    setLimit(MAX_MULTISELECT_RESULT);
                  }}
                  data={dataVyhladavanie}
                  setSelectedItem={handleSelectedItemChange}
                  dataAttributesToShow={[{ key: 'termin', label: '' },
                    { key: 'nazov', label: '' }]}
                  type="column"
                  size="m"
                  visible={showList}
                />
              </div>
            </Col>
          </Row>
        </FormGroup>

        {itemList.length > 0 && (
        <Row>
          <Col width="two-thirds">
            <Button
              type="button"
              size="large"
              className="remove-button"
              buttonStyle="negative"
              onClick={onRemove}
            >
              Zmazať previazanie
            </Button>
          </Col>
        </Row>
        )}
      </div>
      {children && previazanyTermin.previazanyTerminUuid
          && previazanyTermin.previazanyTerminUuid.trim().length > 0
          && <>{children}</>}

    </>
  );
};

export default PreviazanyTerminItem;
